/**
 * Supply: the material register, and the operational procurement flow that
 * hangs off it — RFQs, purchase-order references, deliveries and material
 * inspections.
 */

import { ifMatch, request } from './client.js';
import { pageCall, type RegisterPage } from './paging.js';
// ------------------------------------------------------------------- materials

export type MaterialStatus =
  | 'identified'
  | 'blocked_pending_approval'
  | 'ready_to_order'
  | 'ordered'
  | 'in_production'
  | 'shipped'
  | 'at_site'
  | 'inspected'
  | 'rejected'
  | 'installed'
  | 'cancelled';

export type MaterialRisk = 'none' | 'watch' | 'amber' | 'red';

export interface Material {
  readonly id: string;
  /**
   * The concurrency token, opaque. Hand it back to `updateMaterial` so a save
   * on top of somebody else's is refused rather than silently overwriting it.
   */
  readonly record_version: string;
  readonly number: string;
  readonly description: string;
  readonly specification: string | null;
  readonly quantity: string | null;
  readonly unit: string | null;
  readonly status: MaterialStatus;
  readonly supplier_id: string | null;
  readonly supplier_name: string | null;
  readonly location_id: string | null;
  readonly location_path: string | null;
  readonly submittal_id: string | null;
  readonly submittal_number: string | null;
  readonly submittal_status: string | null;
  readonly required_on_site_date: string | null;
  readonly lead_time_days: number;
  readonly production_days: number;
  readonly shipping_days: number;
  readonly buffer_days: number;
  /** Computed by the database from the dates chain. Read-only, always. */
  readonly order_by_date: string | null;
  readonly eta_date: string | null;
  readonly is_exception: boolean;
  readonly exception_reason: string | null;
  readonly cancelled_reason: string | null;
  readonly risk: MaterialRisk;
  /** Why it is at risk, in plain language. Empty when it is not. */
  readonly risk_reasons: readonly string[];
  readonly allowed_next: readonly MaterialStatus[];
  /** ABSENT, not null, without financial access on this project (ADR-005). */
  readonly unit_cost?: string | null;
  readonly total_cost?: string | null;
  readonly currency?: string | null;
}

export interface MaterialInput {
  readonly description?: string;
  readonly specification?: string | null;
  readonly quantity?: string | null;
  readonly unit?: string | null;
  readonly supplier_name?: string | null;
  readonly location_id?: string | null;
  readonly submittal_id?: string | null;
  readonly required_on_site_date?: string | null;
  readonly lead_time_days?: number;
  readonly production_days?: number;
  readonly shipping_days?: number;
  readonly buffer_days?: number;
  readonly eta_date?: string | null;
  /** Decimal STRINGS, never JSON numbers — a float in a money path is a defect. */
  readonly unit_cost?: string | null;
  readonly total_cost?: string | null;
}

async function materialCall(path: string, init?: RequestInit): Promise<readonly Material[]> {
  const body = await request<{ data: readonly Material[] }>(path, init ?? {});
  return body.data;
}

const matPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/materials`;

/**
 * The register, which the server bounds. Returns the rows AND how many there
 * really are — see `api/paging.ts` for why both halves are needed.
 *
 * The mutations below still return a plain array: they hand back the register
 * as it now stands, and the screen already knows how much of it it had asked
 * for.
 */
export function listMaterials(
  projectId: string,
  limit?: number,
): Promise<RegisterPage<Material>> {
  return pageCall<Material>(matPath(projectId), limit);
}

export function createMaterial(
  projectId: string,
  input: MaterialInput & { description: string },
): Promise<readonly Material[]> {
  return materialCall(matPath(projectId), { method: 'POST', body: JSON.stringify(input) });
}

export function updateMaterial(
  projectId: string,
  materialId: string,
  input: MaterialInput,
  /** The `record_version` this edit was started from. See `ifMatch`. */
  expectedVersion?: string,
): Promise<readonly Material[]> {
  return materialCall(`${matPath(projectId)}/${encodeURIComponent(materialId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
    ...ifMatch(expectedVersion),
  });
}

/** Its own call: making the link is what stops the material. */
export function linkMaterialSubmittal(
  projectId: string,
  materialId: string,
  submittalId: string | null,
): Promise<readonly Material[]> {
  return materialCall(`${matPath(projectId)}/${encodeURIComponent(materialId)}/submittal`, {
    method: 'PATCH',
    body: JSON.stringify({ submittal_id: submittalId }),
  });
}

export function moveMaterial(
  projectId: string,
  materialId: string,
  input: { status: MaterialStatus; reason?: string },
): Promise<readonly Material[]> {
  return materialCall(`${matPath(projectId)}/${encodeURIComponent(materialId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteMaterial(
  projectId: string,
  materialId: string,
): Promise<readonly Material[]> {
  return materialCall(`${matPath(projectId)}/${encodeURIComponent(materialId)}`, {
    method: 'DELETE',
  });
}


// ---------------------------------------------------------------------------
// Procurement: RFQs, POs, deliveries, material inspections
// ---------------------------------------------------------------------------

export interface Rfq {
  readonly id: string;
  readonly supplier_name: string;
  readonly sent_on: string;
  readonly responded_on: string | null;
  readonly status: 'sent' | 'quoted' | 'declined' | 'withdrawn';
  readonly note: string | null;
  /** Absent without financial access. */
  readonly quoted_amount?: string | null;
}

export interface PurchaseOrderRef {
  readonly id: string;
  readonly po_number: string;
  readonly po_date: string;
  readonly supplier_name: string | null;
  readonly document_id: string | null;
  readonly status: 'issued' | 'cancelled';
  readonly note: string | null;
  readonly po_value?: string | null;
}

export interface Delivery {
  readonly id: string;
  readonly delivered_on: string;
  readonly quantity_received: string | null;
  readonly received_by_name: string | null;
  readonly damage_notes: string | null;
  readonly note: string | null;
}

export interface MaterialInspection {
  readonly id: string;
  readonly inspected_on: string;
  readonly result: 'accepted' | 'accepted_with_notes' | 'rejected';
  readonly notes: string | null;
  readonly inspector_name: string | null;
  readonly delivery_id: string | null;
}

export interface ProcurementView {
  readonly rfqs: readonly Rfq[];
  readonly purchase_orders: readonly PurchaseOrderRef[];
  readonly deliveries: readonly Delivery[];
  readonly inspections: readonly MaterialInspection[];
}

const procurementPath = (projectId: string, materialId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/materials/${encodeURIComponent(materialId)}`;

export function getProcurement(
  projectId: string,
  materialId: string,
): Promise<ProcurementView> {
  return request<ProcurementView>(`${procurementPath(projectId, materialId)}/procurement`);
}

export function addRfq(
  projectId: string,
  materialId: string,
  input: { supplier_name: string; sent_on: string; note?: string | null },
): Promise<ProcurementView> {
  return request<ProcurementView>(`${procurementPath(projectId, materialId)}/rfqs`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function answerRfq(
  projectId: string,
  rfqId: string,
  input: { status: string; quoted_amount?: string | null; responded_on?: string | null },
): Promise<ProcurementView> {
  return request<ProcurementView>(
    `/v1/projects/${encodeURIComponent(projectId)}/rfqs/${encodeURIComponent(rfqId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
  );
}

/** Recording the PO IS ordering — the material becomes `ordered` with it. */
export function recordPurchaseOrder(
  projectId: string,
  materialId: string,
  input: {
    po_number: string;
    po_date: string;
    po_value?: string | null;
    supplier_name?: string | null;
    document_id?: string | null;
    note?: string | null;
  },
): Promise<ProcurementView> {
  return request<ProcurementView>(`${procurementPath(projectId, materialId)}/purchase-orders`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function recordDelivery(
  projectId: string,
  materialId: string,
  input: {
    delivered_on: string;
    quantity_received?: string | null;
    damage_notes?: string | null;
    note?: string | null;
  },
): Promise<ProcurementView> {
  return request<ProcurementView>(`${procurementPath(projectId, materialId)}/deliveries`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** QA/QC's verdict — and the at_site → inspected/rejected move with it. */
export function recordMaterialInspection(
  projectId: string,
  materialId: string,
  input: {
    result: string;
    inspected_on: string;
    notes?: string | null;
    delivery_id?: string | null;
  },
): Promise<ProcurementView> {
  return request<ProcurementView>(`${procurementPath(projectId, materialId)}/inspections`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
